import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Lock, Check, Copy, Smartphone, IndianRupee, Loader2, LogOut, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Paywall = ({ onActivated }) => {
  const auth = useAuth();
  const user = auth?.user;
  const logout = auth?.logout || (() => { localStorage.clear(); window.location.href = '/login'; });
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [txnRef, setTxnRef] = useState('');
  const [upiApp, setUpiApp] = useState('PhonePe');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try { const r = await apiService.subscriptionStatus(); setStatus(r.data); }
    catch (e) { /* fallback static */ setStatus({ monthly_price_inr: 1500, upi_id: '9911777700@upi', upi_number: '9911777700', payee_name: 'Ekamraj Productions' }); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied: ${text}`);
  };

  const submitClaim = async () => {
    if (!txnRef.trim() || txnRef.trim().length < 4) {
      toast.error('Please enter the UPI transaction reference (at least 4 chars)');
      return;
    }
    setSubmitting(true);
    try {
      await apiService.claimPayment({
        transaction_ref: txnRef.trim(),
        upi_app: upiApp,
        amount: status?.monthly_price_inr || 1500,
      });
      toast.success('Payment recorded! You now have full access for 30 days.');
      if (onActivated) onActivated();
      else window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally { setSubmitting(false); }
  };

  if (!status) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-indigo-500" /></div>;

  const upiPaymentUri = `upi://pay?pa=${encodeURIComponent(status.upi_id)}&pn=${encodeURIComponent(status.payee_name)}&am=${status.monthly_price_inr}&cu=INR&tn=SkillRise%20Subscription`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Top bar */}
        <div className="flex justify-end mb-4">
          <button onClick={logout} data-testid="paywall-logout-btn"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 font-semibold text-sm">
            <LogOut className="w-4 h-4" />Logout
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-200">
          
          {/* Hero */}
          <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 text-white p-8 sm:p-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 rounded-full text-xs font-bold mb-4">
              <Lock className="w-3 h-3" />SUBSCRIPTION REQUIRED
            </div>
            <h1 className="text-3xl sm:text-4xl font-black mb-2">Unlock SkillRise Premium</h1>
            <p className="text-white/70 text-sm sm:text-base max-w-2xl">
              Hi {user?.username}, your full access to chapters, AI tutoring, voice calls, worksheets, books library, homework scanner and more is just one payment away.
            </p>
          </div>

          <div className="p-6 sm:p-10 grid md:grid-cols-2 gap-8">
            {/* Plan + QR */}
            <div>
              <h2 className="font-black text-lg text-[#1E293B] mb-4">Monthly Plan</h2>
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border-2 border-indigo-200 mb-5">
                <div className="flex items-baseline gap-1 mb-1">
                  <IndianRupee className="w-7 h-7 text-indigo-700" />
                  <span className="text-5xl font-black text-indigo-900">{status.monthly_price_inr}</span>
                  <span className="text-slate-500 font-bold">/ month</span>
                </div>
                <p className="text-sm text-slate-600 mb-5">30 days of full access. Cancel anytime.</p>
                
                <ul className="space-y-2.5">
                  {[
                    'Full NCERT chapter coverage with tests',
                    'AI text and voice tutor',
                    'AI worksheet generator',
                    'Homework photo scanner with AI grading',
                    'Unlimited books library with AI Q&A',
                    'Math workspace and learning games',
                    'Story-quest learning games',
                    'Emotional check-ins and analytics',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* QR Code */}
              <div className="bg-white border-2 border-slate-200 rounded-2xl p-5 text-center">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Scan to pay</p>
                <div className="inline-block p-3 bg-white border-2 border-slate-200 rounded-2xl" data-testid="upi-qr">
                  <QRCodeCanvas value={upiPaymentUri} size={170} level="M" includeMargin={false} />
                </div>
                <p className="text-xs text-slate-500 mt-3">Open any UPI app and scan this QR</p>
              </div>
            </div>

            {/* Pay via UPI */}
            <div>
              <h2 className="font-black text-lg text-[#1E293B] mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-600" />Pay via UPI
              </h2>
              <div className="bg-white border-2 border-slate-200 rounded-2xl p-5">
                <p className="text-sm text-slate-600 mb-4">Send <span className="font-black text-indigo-700">₹{status.monthly_price_inr}</span> to either of these and submit the transaction reference below:</p>
                
                {/* UPI ID / Number */}
                <div className="space-y-2 mb-5">
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">UPI ID</p>
                      <p className="font-mono font-bold text-[#1E293B]" data-testid="upi-id">{status.upi_id}</p>
                    </div>
                    <button onClick={() => copy(status.upi_id)} className="p-2 hover:bg-slate-200 rounded-lg" data-testid="copy-upi-id">
                      <Copy className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">UPI / Phone Number</p>
                      <p className="font-mono font-bold text-[#1E293B]" data-testid="upi-number">{status.upi_number}</p>
                    </div>
                    <button onClick={() => copy(status.upi_number)} className="p-2 hover:bg-slate-200 rounded-lg" data-testid="copy-upi-number">
                      <Copy className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Payee Name</p>
                      <p className="font-bold text-[#1E293B]">{status.payee_name}</p>
                    </div>
                  </div>
                </div>

                {/* Open UPI app deep link (mobile) */}
                <a href={upiPaymentUri} data-testid="upi-pay-link"
                  className="block w-full text-center py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold mb-5 shadow-md">
                  Pay ₹{status.monthly_price_inr} via UPI App
                </a>

                <div className="border-t border-slate-200 pt-4">
                  <p className="text-xs font-bold text-slate-500 mb-3 uppercase">After payment, enter the details below</p>
                  
                  <label className="block text-xs font-bold text-slate-600 mb-1">UPI App Used</label>
                  <select value={upiApp} onChange={e => setUpiApp(e.target.value)} data-testid="upi-app-select"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium mb-3">
                    <option>PhonePe</option><option>Google Pay</option><option>Paytm</option>
                    <option>BHIM</option><option>Other</option>
                  </select>
                  
                  <label className="block text-xs font-bold text-slate-600 mb-1">Transaction / UTR Reference</label>
                  <input type="text" value={txnRef} onChange={e => setTxnRef(e.target.value)} data-testid="txn-ref-input"
                    placeholder="e.g. 234567891011"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono mb-4 focus:border-indigo-500 outline-none" />
                  
                  <motion.button onClick={submitClaim} disabled={submitting} whileTap={{ scale: 0.98 }} data-testid="submit-payment-btn"
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-xl font-black disabled:opacity-50 flex items-center justify-center gap-2 shadow-md">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Activating...</> : <><ShieldCheck className="w-4 h-4" />Activate My Subscription</>}
                  </motion.button>
                </div>
              </div>
              
              <p className="text-[11px] text-slate-500 mt-3 text-center">
                Your subscription activates instantly. Our team reviews payments and may revoke access if the transaction is not verified.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Paywall;
